import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PageHeader, DetailCard, LoadingState } from '../components';
import { useUnits, useCreateUnit, useConversions, useCreateConversion } from '../hooks/useUnitsOfMeasure';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors } from '../constants';

const UNIT_CATEGORIES = [
  { value: 'weight', label: 'Weight' },
  { value: 'volume', label: 'Volume' },
  { value: 'length', label: 'Length' },
  { value: 'count', label: 'Count' },
  { value: 'other', label: 'Other' },
];

export default function UnitsOfMeasure() {
  const businessUuid = useBusinessUuid();
  const { data: units = [], isLoading } = useUnits();
  const { data: conversions = [] } = useConversions();
  const createUnit = useCreateUnit();
  const createConversion = useCreateConversion();

  const [showAddUnit, setShowAddUnit] = useState(false);
  const [unitName, setUnitName] = useState('');
  const [unitAbbr, setUnitAbbr] = useState('');
  const [unitCat, setUnitCat] = useState('weight');

  const [showAddConv, setShowAddConv] = useState(false);
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [conversionFactor, setConversionFactor] = useState('');
  const [toast, setToast] = useState(null);

  const unitOptions = useMemo(
    () => units.map((u) => ({ value: u.uuid, label: `${u.name} (${u.abbreviation})` })),
    [units]
  );

  const handleAddUnit = async () => {
    if (!unitName.trim() || !unitAbbr.trim()) return;
    try {
      await createUnit.mutateAsync({ businessUuid, name: unitName, abbreviation: unitAbbr, category: unitCat });
      setShowAddUnit(false);
      setUnitName('');
      setUnitAbbr('');
      setToast({ severity: 'success', message: 'Unit created' });
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Something went wrong' });
    }
  };

  const handleAddConversion = async () => {
    if (!fromUnit || !toUnit || !conversionFactor) return;
    try {
      await createConversion.mutateAsync({
        businessUuid,
        fromUnitUuid: fromUnit,
        toUnitUuid: toUnit,
        conversionFactor: parseFloat(conversionFactor) || 1,
      });
      setShowAddConv(false);
      setConversionFactor('');
      setToast({ severity: 'success', message: 'Conversion created' });
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Something went wrong' });
    }
  };

  if (isLoading) return <LoadingState />;

  return (
    <>
      <PageHeader title="Units of Measure" backTo="/inventory-management/settings" />
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <DetailCard
            title="Units"
            action={
              <Button size="small" startIcon={<AddIcon />} onClick={() => setShowAddUnit((s) => !s)} sx={{ textTransform: 'none' }}>
                Add
              </Button>
            }
          >
            {showAddUnit && (
              <Box sx={{ p: 2, mb: 2, borderRadius: 2, backgroundColor: colors.bg }}>
                <Stack spacing={2}>
                  <TextField label="Name" required value={unitName} onChange={(e) => setUnitName(e.target.value)} size="small" placeholder="Kilogram" />
                  <TextField label="Abbreviation" required value={unitAbbr} onChange={(e) => setUnitAbbr(e.target.value)} size="small" placeholder="kg" />
                  <TextField select label="Category" value={unitCat} onChange={(e) => setUnitCat(e.target.value)} size="small">
                    {UNIT_CATEGORIES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                    ))}
                  </TextField>
                  <Button variant="contained" onClick={handleAddUnit} disabled={createUnit.isPending} sx={{ backgroundColor: colors.primary, textTransform: 'none' }}>
                    Save
                  </Button>
                </Stack>
              </Box>
            )}
            {units.map((u) => (
              <Stack key={u.uuid} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: `1px solid ${colors.gray100}` }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{u.name}</Typography>
                  <Typography variant="caption" sx={{ color: colors.gray500 }}>{u.category}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: colors.gray600 }}>{u.abbreviation}</Typography>
              </Stack>
            ))}
            {units.length === 0 && !showAddUnit && (
              <Typography variant="body2" sx={{ color: colors.gray500 }}>No units defined</Typography>
            )}
          </DetailCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <DetailCard
            title="Conversions"
            action={
              <Button size="small" startIcon={<AddIcon />} onClick={() => setShowAddConv((s) => !s)} sx={{ textTransform: 'none' }}>
                Add
              </Button>
            }
          >
            {showAddConv && (
              <Box sx={{ p: 2, mb: 2, borderRadius: 2, backgroundColor: colors.bg }}>
                <Stack spacing={2}>
                  <TextField select label="From Unit" required value={fromUnit} onChange={(e) => setFromUnit(e.target.value)} size="small">
                    {unitOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField select label="To Unit" required value={toUnit} onChange={(e) => setToUnit(e.target.value)} size="small">
                    {unitOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Conversion Factor"
                    required
                    type="number"
                    value={conversionFactor}
                    onChange={(e) => setConversionFactor(e.target.value)}
                    size="small"
                    placeholder="1000"
                    helperText="1 from = factor * to"
                  />
                  <Button variant="contained" onClick={handleAddConversion} disabled={createConversion.isPending} sx={{ backgroundColor: colors.primary, textTransform: 'none' }}>
                    Save
                  </Button>
                </Stack>
              </Box>
            )}
            {conversions.map((c) => {
              const from = units.find((u) => u.uuid === c.fromUnitUuid);
              const to = units.find((u) => u.uuid === c.toUnitUuid);
              return (
                <Box key={c.uuid} sx={{ py: 1, borderBottom: `1px solid ${colors.gray100}` }}>
                  <Typography variant="body2">
                    1 {from?.abbreviation || c.fromUnitUuid} = {c.conversionFactor} {to?.abbreviation || c.toUnitUuid}
                  </Typography>
                </Box>
              );
            })}
            {conversions.length === 0 && !showAddConv && (
              <Typography variant="body2" sx={{ color: colors.gray500 }}>No conversions defined</Typography>
            )}
          </DetailCard>
        </Grid>
      </Grid>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </>
  );
}
